import React, { useCallback, forwardRef } from "react";
import { Link, LinkProps } from "react-router";

export interface HashPreservingLinkProps extends LinkProps {
  /**
   * Optional hash id to set on the current URL before navigating away.
   */
  fromHash?: string;
}

/**
 * A link that optionally updates the current URL's hash before navigating, enabling scroll restoration when the user presses back.
 *
 * If `fromHash` is provided, the current URL's hash will be set to this value before navigation.
 */
export const HashPreservingLink = forwardRef<HTMLAnchorElement, HashPreservingLinkProps>(
  ({ fromHash, onClick, ...props }, ref) => {
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (onClick) onClick(e);
        if (e.defaultPrevented) return;
        if (fromHash && window.location.hash !== `#${fromHash}`) {
          window.history.replaceState(null, "", `#${fromHash}`);
        }
      },
      [fromHash, onClick],
    );

    return <Link {...props} onClick={handleClick} ref={ref} />;
  },
);
HashPreservingLink.displayName = "HashPreservingLink";
